import { FirebaseError, initializeApp } from "firebase/app";
import {
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail as sendPasswordResetEmailToUser,
  GoogleAuthProvider,
  signInWithPopup,
  type User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword as signInUserWithEmailAndPassword,
  SAMLAuthProvider,
  OAuthProvider,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  type UserCredential,
} from "firebase/auth";
import environment from "@/environment";
import type { SetupContext } from "vue";
import { ref, shallowRef } from "vue";
import messages, { translateWithParams } from "@/plugins/i18n";
import errorLoggerService from "@/app/errors/errorLoggerService";
import type { AccountLinkEnterPasswordDialogPayload } from "@/components/pages/account/subcomponents/AccountLinkEnterPasswordDialog.vue";
import type { DialogResult } from "@/components/platform/common/showModalDialog";

interface VerificationEmailUrlProperties {
  path?: string;
  params?: {
    name: string;
    value: string;
  }[];
}

initializeApp({
  apiKey: environment.firebaseApiKey.toString(),
  authDomain: environment.firebaseAuthDomain.toString(),
});

export const EmailVerificationCallbackSignInQueryStringName = "signin";
const verifiedEmailLandingPagePath = "/account/verifiedemail";

const authProvider = ref<string>("");

const auth = getAuth();
auth.onAuthStateChanged((user) => {
  currentGipUser.value = user;

  // Repopulate auth provider data taken from current user
  if (currentGipUser.value?.providerData.length === 1) {
    authProvider.value = currentGipUser.value.providerData[0].providerId;
  }
});

// Note: these variables are accessed in userStore, so should not
// live inside a composable
export const currentGipUser = shallowRef<User | null>(null);
export const signOut = async () => await auth.signOut();

export function useAuthentication(
  validateEmailPasswordForm?: () => boolean,
  validateForgottenPasswordForm?: () => boolean,
  context?: SetupContext,
  openAccountLinkEnterPasswordDialog?: (
    email: string
  ) => Promise<DialogResult<AccountLinkEnterPasswordDialogPayload>>
) {
  const originUrl = window.location.origin;
  const signInError = ref("");
  const forgottenPasswordResetEmailSent = ref(false);

  const emitSuccess = (user: User | null) => context?.emit("success", user);

  const firebaseErrorsToSuppress: string[] = [
    "auth/cancelled-popup-request",
    "auth/popup-closed-by-user",
  ];

  /**
   * Attempts to translate the error message if the message contains a known
   * code, otherwise returns the error message, or the optional fallback value
   */
  const translateErrorMessage = ({
    errorMessage,
    fallback,
  }: {
    errorMessage: string;
    fallback?: string;
  }) => {
    return (
      // Update this object with any custom translations for standard firebase
      // auth errors. A full list of error codes can be found here:
      // https://firebase.google.com/docs/reference/js/auth#autherrorcodes
      [
        { s: "auth/weak-password", t: messages.auth_weak_password },
        { s: "auth/email-already-in-use", t: messages.auth_email_in_use },
        { s: "auth/invalid-email", t: messages.auth_invalid_email },
        { s: "auth/wrong-password", t: messages.auth_wrong_password },
      ].find((x) => errorMessage.includes(x.s))?.t ??
      fallback ??
      errorMessage
    );
  };

  const wrapAuthenticationWithErrorHandling = async (
    callback: () => PromiseLike<void>
  ): Promise<void> => {
    try {
      await callback();
    } catch (error: any) {
      if (error instanceof FirebaseError) {
        errorLoggerService.logInfo(
          "SESSION - Firebase auth failed : " + error.code
        );
        // suppress irrelevant errors
        if (firebaseErrorsToSuppress.includes(error.code)) {
          return;
        }

        if (error.code === "auth/account-exists-with-different-credential") {
          /**
           * A verified provider is the original provider associated with the email
           * address that the user attempted to log in with (e.g. user originally
           * signed in with Google, but has now attempted to sign in with Microsoft,
           * using the same email address. Google is the verified provider).
           *
           * Different providers using the same email address can be linked together
           * in firebase, but this has to be done manually.
           *
           * See: https://cloud.google.com/identity-platform/docs/link-accounts
           */

          const email = error.customData?.email as string | undefined;

          if (!email) {
            signInError.value = messages.auth_no_email_from_auth_provider;
            return;
          }

          // NB: The current Firebase docs are outdated - this is how to retrieve
          // the OAuth credential object:
          // https://github.com/firebase/firebase-js-sdk/issues/5057
          const credential = OAuthProvider.credentialFromError(error);

          if (!credential) {
            signInError.value = translateWithParams(
              "auth_no_credential_found_for_email",
              {
                email,
              }
            ).toString();
            return;
          }

          const auth = getAuth();

          const alternativeSignInMethods = await fetchSignInMethodsForEmail(
            auth,
            email
          );

          if (alternativeSignInMethods.length === 0) {
            signInError.value = translateWithParams(
              "auth_no_alternative_sign_in_methods_for_email",
              {
                email,
              }
            ).toString();
            return;
          }

          if (alternativeSignInMethods[0] === "password") {
            if (!openAccountLinkEnterPasswordDialog) {
              return;
            }

            // Prompt the user for a password
            const { value, wasSuccessful } =
              await openAccountLinkEnterPasswordDialog(email);

            if (!value || !wasSuccessful) {
              signInError.value = messages.auth_no_password_entered;
              return;
            }

            let userCredential: UserCredential | null = null;

            try {
              // Authenticate with Firebase using email/password
              userCredential = await signInUserWithEmailAndPassword(
                auth,
                email,
                value.password
              );

              // Perform account link
              await linkWithCredential(userCredential.user, credential);
            } catch (e: any) {
              signInError.value = translateErrorMessage({
                errorMessage: e.toString(),
              });
              return;
            }

            // Sanity check - this should always be true
            if (!userCredential) {
              signInError.value = translateWithParams(
                "auth_failed_to_get_credential_for_email",
                {
                  email,
                }
              ).toString();
              return;
            }

            // Successfully authenticated with Firebase and linked accounts,
            // now log in to the app
            emitSuccess(userCredential.user);
            return;
          }

          // There are numerous other alternative sign in methods, and each needs
          // to be handled explicitly. For now, show an error to the user that
          // details the alternative methods that they have signed in with before,
          // but are currently not supported.
          signInError.value = translateWithParams(
            "auth_account_linking_for_methods_not_supported",
            {
              methods: alternativeSignInMethods.join(", "),
            }
          ).toString();
          return;
        }

        signInError.value = translateErrorMessage({
          errorMessage: error.code,
          fallback: error.message,
        });
      } else {
        errorLoggerService.logInfo(
          "SESSION - Authentication failed : " +
            error.toString().substring(0, 100)
        );

        signInError.value = error.toString();
      }
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    const provider = new GoogleAuthProvider();
    // Ensure user is prompted to select which Google account
    // to sign in with
    provider.setCustomParameters({
      prompt: "select_account",
    });
    const auth = getAuth();
    await wrapAuthenticationWithErrorHandling(async () => {
      errorLoggerService.logInfo("SESSION - Opening Google Sign In Popup");
      const userCredential = await signInWithPopup(auth, provider);
      errorLoggerService.logInfo(
        "SESSION - Google Popup return : " +
          (userCredential?.user?.email ?? "[no user email]")
      );
      emitSuccess(userCredential.user);
    });
  };

  const signUpWithEmailAndPassword = async (
    email: string,
    password: string
  ): Promise<void> => {
    if (
      validateEmailPasswordForm?.() &&
      email.length > 0 &&
      password.length > 0
    ) {
      const auth = getAuth();
      await wrapAuthenticationWithErrorHandling(async () => {
        let userCredential: UserCredential | null = null;

        // Attempt to create a new user account, or log in to exising account
        // if the email address is already in use
        try {
          userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
          );
        } catch (e: any) {
          if (
            e instanceof FirebaseError &&
            e.code === "auth/email-already-in-use"
          ) {
            return await signInWithEmailAndPassword(email, password);
          }

          throw new Error(e);
        }

        emitSuccess(userCredential.user);
      });
    }
  };

  const signInWithEmailAndPassword = async (
    email: string,
    password: string
  ): Promise<void> => {
    if (
      validateEmailPasswordForm?.() &&
      email.length > 0 &&
      password.length > 0
    ) {
      const auth = getAuth();
      await wrapAuthenticationWithErrorHandling(async () => {
        const userCredential = await signInUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        emitSuccess(userCredential.user);
      });
    }
  };

  const signInWithMicrosoft = async (): Promise<void> => {
    const provider = new OAuthProvider("microsoft.com");

    // Ensure user is prompted to select which Microsoft account
    // to sign in with
    provider.setCustomParameters({
      prompt: "select_account",
    });

    const auth = getAuth();
    await wrapAuthenticationWithErrorHandling(async () => {
      const userCredential = await signInWithPopup(auth, provider);
      emitSuccess(userCredential.user);
    });
  };

  /**
   * WARNING: this is untested as our Okta trial account expired
   * 30/12/2021.
   *
   * Identity Platform is fully set up with provider id "saml.greene-king".
   * We were previously able to successfully authenticate using FirebaseUI
   * SAML sign in options.
   *
   * For more details about Okta/GCP SAML setup:
   * https://docs.google.com/document/d/1yBcyph7kJ_0mkwkyuuDBV1yrwCV_FnxkYCLeOs1sdEk/edit?usp=sharing
   * https://docs.google.com/document/d/18aCSMchqNPz-DnepX_okopoij3owjF4HRixlv0hjL_c/edit?usp=sharing
   * https://cloud.google.com/identity-platform/docs/web/saml#popup
   */
  const signInWithSaml = async (providerId: string): Promise<void> => {
    const provider = new SAMLAuthProvider(`saml.${providerId}`);
    const auth = getAuth();
    await wrapAuthenticationWithErrorHandling(async () => {
      const userCredential = await signInWithPopup(auth, provider);
      emitSuccess(userCredential.user);
    });
  };

  const sendEmailVerificationEmail = async (
    properties?: VerificationEmailUrlProperties
  ): Promise<void> => {
    if (!currentGipUser.value) {
      return;
    }

    const path = properties?.path ?? verifiedEmailLandingPagePath;
    const url = new URL(path, originUrl);

    if (properties?.params) {
      properties.params.forEach(({ name, value }) =>
        url.searchParams.append(name, value)
      );
    }

    await sendEmailVerification(currentGipUser.value, {
      url: url.href,
    });
  };

  const sendPasswordResetEmail = async (): Promise<boolean> => {
    if (!currentGipUser.value?.email) {
      return false;
    }

    await sendPasswordResetEmailToUser(auth, currentGipUser.value.email, {
      url: new URL("/", originUrl).href,
    });

    return true;
  };

  const sendForgottenPasswordEmail = async (
    email: string,
    event?: Event
  ): Promise<void> => {
    event?.preventDefault();

    if (validateForgottenPasswordForm?.()) {
      try {
        await sendPasswordResetEmailToUser(auth, email, {
          url: new URL("/", originUrl).href,
        });
      } catch (e: any) {
        // don't show actual error to user - only a generic message
        // is shown, whether the password reset email sends or not
        errorLoggerService.logError(
          `Password reset email failed to send to ${email}`,
          e
        );
      } finally {
        forgottenPasswordResetEmailSent.value = true;
      }
    }
  };

  const clearSignInError = (): void => {
    signInError.value = "";
  };

  return {
    signInWithGoogle,
    signUpWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerificationEmail,
    signOut,
    sendPasswordResetEmail,
    sendForgottenPasswordEmail,
    auth,
    currentGipUser,
    authProvider,
    signInError,
    clearSignInError,
    forgottenPasswordResetEmailSent,
    signInWithSaml,
    signInWithMicrosoft,
  };
}
